import '../../../styles/Stand/Reports.css';

import { EditOutlined }         from '@ant-design/icons';
import { CheckOutlined }        from '@ant-design/icons';
import { DownloadOutlined }     from '@ant-design/icons';
import { PlusOutlined }         from '@ant-design/icons';
import { WarningOutlined }      from '@ant-design/icons';
import { AppForm }              from 'components/App/AppForm';
import { ReportForm }           from 'forms/ReportForm';
import { observer }             from 'mobx-react';
import { Button }               from 'antd';
import { Card }                 from 'antd';
import { Col }                  from 'antd';
import Row                      from 'components/App/Row';
import dayjs                    from 'dayjs';
import { Modal }                from 'antd';
import { appStore }             from 'stores/AppStore';
import { standDashboardStore }  from 'stores/index';
import { formatMessageIntl }    from 'utils/messageIntl';
import { messageSuccessIntl }   from 'utils/messageIntl';
import { messageErrorIntl }     from 'utils/messageIntl';
import { toSafeMessageHtml }    from 'utils/richTextContent';
import rights                   from 'utils/rights';
import { REPORT_STATUS }        from 'utils/constants';
import FormattedMessageWrapper  from 'components/App/FormattedMessageWrapper';
import { downloadFileFromBlob } from 'utils/downloadFile';
import { ApiCollection }        from 'collections/ApiCollection';
import ReportModel              from 'models/ReportModel';
import ReportPdfModel           from 'models/ReportPdfModel';
import ReportMediaModel         from 'models/ReportMediaModel';

const styleIcon = {
  fontSize: '22px',
  marginRight: 6,
};

interface StandDashboardConstatsBoxProps {
  fetchParentData: () => void;
  setDataLoading: (loading: boolean) => void;
}

const StandDashboardConstatsBox = ({
  fetchParentData,
  setDataLoading,
}: StandDashboardConstatsBoxProps) => {
  const {
    collReport,
    stand,
  } = standDashboardStore;

  const closeReport = (report: ReportModel) => {
    Modal.confirm({
      cancelText: formatMessageIntl('Annuler'),
      content: formatMessageIntl('Confirmez-vous la clôture de ce constat ?'),
      okText: formatMessageIntl('Oui'),
      onOk: async () => {
        try {
          await report.patch({ status: REPORT_STATUS.CLOSED });
          messageSuccessIntl("Constat clôturé");
          fetchParentData();
        } catch {
          messageErrorIntl("Une erreur s'est produite.");
        }
      },
      title: formatMessageIntl('Clôturer un constat'),
    });
  };

  const downloadReportPdf = async (report: ReportModel, locale: string) => {
    const {
      show,
      stand,
    } = standDashboardStore;

    setDataLoading(true);

    const reportPdf = new ReportPdfModel();
    reportPdf.set({ id: report.id, locale });

    try {
      const response = await reportPdf.fetch() as any;
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const time = dayjs().format('YYYY-MM-DD_HHmm');
      const filename = `${[show.name, stand.name, report.id, time].join('_')}.pdf`;
      downloadFileFromBlob(blob, filename);
    } catch {
      messageErrorIntl("Une erreur s'est produite.");
    }

    setDataLoading(false);
  };

  const editReport = async (report: ReportModel) => {
    setDataLoading(true);

    const reportMediaCollection = new ApiCollection(ReportMediaModel);
    reportMediaCollection.setFilter('report', report.get('id'));

    try {
      await reportMediaCollection.list();

      AppForm.open(ReportForm, {
        report,
        reportMedias: reportMediaCollection.models,
        stand: standDashboardStore.stand,
        onSuccess: () => {
          fetchParentData();
        }
      }, {
        title: <FormattedMessageWrapper id="Modifier constat" />,
        width: 768,
      });
    } catch {
      messageErrorIntl("Une erreur s'est produite.");
    } finally {
      setDataLoading(false);
    }
  };

  return (
    <Card
      className="accented-shadow"
      title={
        <div
          style={{
            alignItems: 'center',
            display: 'flex',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', flex: 1 }}>
            <WarningOutlined style={{ ...styleIcon, color: '#f1c40f' }} />
            <span>
                <FormattedMessageWrapper id="Constats" />
              </span>
          </div>
          {rights.for.report.add && (
            <div>
              <Button
                icon={<PlusOutlined />}
                htmlType="button"
                onClick={() => {
                  AppForm.open(ReportForm, {
                    onSuccess: () => {
                      fetchParentData();
                    },
                    report: new ReportModel(),
                    reportMedias: [],
                    stand,
                  }, {
                    title: <FormattedMessageWrapper id="Nouveau constat" />,
                    width: 768,
                  });
                }}
                style={{
                  alignItems: 'center',
                  display: 'flex',
                  gap: '8px',
                }}
              >
                <FormattedMessageWrapper id="Ajouter un constat" />
              </Button>
            </div>
          )}
        </div>
      }
    >
      <p>
        <FormattedMessageWrapper
          id="{number} constat(s)"
          values={{ number: collReport.total }}
        />
      </p>
      <ul className="reports-list">
        {collReport.models.map((report: ReportModel) => (
          <li key={report.id}>
            <div>
              <Row
                style={{ alignItems: 'center', marginBottom: 5, }}
              >
                <Col
                  className={
                    report.status === REPORT_STATUS.IN_PROGRESS
                      ? 'report-pending'
                      : 'report-closed'
                  }
                  style={{ flex: 1 }}
                >
                  {report.name} (
                  {report.status === REPORT_STATUS.IN_PROGRESS ? (
                    <FormattedMessageWrapper id="constat en cours" />
                  ) : (
                    <FormattedMessageWrapper id="constat clôturé" />
                  )}
                  )
                </Col>
                <Col
                  style={{
                    display: 'flex',
                    gap: '8px',
                    justifyContent: 'flex-end',
                  }}
                >
                  {
                    (report.status === REPORT_STATUS.IN_PROGRESS
                      && rights.for.report.editOrClose) && (
                      <>
                        <Button
                          htmlType="button"
                          icon={<EditOutlined />}
                          onClick={() => editReport(report)}
                        />
                        <Button
                          htmlType="button"
                          icon={<CheckOutlined />}
                          onClick={() => closeReport(report)}
                        />
                      </>
                    )}
                  <Button
                    htmlType="button"
                    icon={<DownloadOutlined />}
                    onClick={() => downloadReportPdf(report, appStore.locale)}
                    style={{
                      alignItems: 'center',
                      display: 'flex',
                      gap: '8px',
                    }}
                  >
                    <FormattedMessageWrapper id="Télécharger" />
                  </Button>
                </Col>
              </Row>
            </div>
            <div
              dangerouslySetInnerHTML={{ __html: toSafeMessageHtml(report.comment) }}
              style={{ padding: 5, overflowWrap: 'break-word', wordBreak: 'normal' }}
            />
          </li>
        ))}
      </ul>
    </Card>
  );
};

const StandDashboardConstatsBoxObserver = observer(StandDashboardConstatsBox);

export default StandDashboardConstatsBoxObserver;
